/**
 * The Facebook listing.
 *
 * Two halves, deliberately. The head is generated from the database and is
 * always correct: year, make, model, trim, mileage, price, and the out-the-
 * door figure. The body is yours — condition, equipment, the lines you keep
 * or delete per car — and this file does not invent any of it.
 *
 * The split matters because the head contains claims a buyer can hold you to
 * and the state can check, while the body is description. Nothing here
 * asserts a feature, an owner count or a Carfax result that is not already
 * recorded somewhere.
 */

import { computeDeal } from "./tax";
import { formatMoney } from "./money";

export interface ListingVehicle {
  year: number;
  make: string;
  model: string;
  trim_level: string | null;
  odometer: number;
  vin: string;
  asking_price_cents: number;
  /** The edited description, kept on the vehicle. Null before it is opened. */
  listing_body: string | null;
}

export interface ListingFees {
  /** Charged on every deal and taxable in Florida. */
  predeliveryServiceCents: number;
  electronicFilingCents: number;
  /** Government charges, not taxed. */
  titleFeeCents: number;
  /** Transferring a plate the buyer already has. */
  tagTransferCents: number;
  /** A brand new plate instead. */
  newTagCents: number;
  countySurtaxBps: number;
}

export interface OutTheDoor {
  priceCents: number;
  taxCents: number;
  /** Filing, title and registration — everything that is not price or tax. */
  ownTagFeesCents: number;
  newTagFeesCents: number;
  withOwnTagCents: number;
  withNewTagCents: number;
}

/**
 * What the buyer actually hands over.
 *
 * Uses the same computeDeal the contract uses, so the advertised figure and
 * the one on the paperwork cannot disagree. A flat percentage cannot do this:
 * the county surtax applies only to the first $5,000 of the vehicle, so 6.5%
 * overstates the tax on anything dearer — by $75 on a $20,000 car.
 */
export function outTheDoor(priceCents: number, fees: ListingFees): OutTheDoor {
  const base = {
    cashPrice: priceCents,
    predeliveryService: fees.predeliveryServiceCents,
    electronicFiling: fees.electronicFilingCents,
    titleFee: fees.titleFeeCents,
    tempTag: 0,
    tradeAllowance: 0, tradePayoff: 0, cashDown: 0, deferredDown: 0,
    countySurtaxBps: fees.countySurtaxBps,
  };

  const ownTag = computeDeal({ ...base, licenseRegistration: fees.tagTransferCents });
  const newTag = computeDeal({ ...base, licenseRegistration: fees.newTagCents });

  const taxCents = ownTag.stateTax + ownTag.surtax;
  return {
    priceCents,
    taxCents,
    /* Whatever is left once price and tax are taken out, so the three
       figures always add up to the total shown beside them. A breakdown
       that does not reconcile is worse than no breakdown. */
    ownTagFeesCents: ownTag.totalAmountDue - priceCents - taxCents,
    newTagFeesCents: newTag.totalAmountDue - priceCents - taxCents,
    withOwnTagCents: ownTag.totalAmountDue,
    withNewTagCents: newTag.totalAmountDue,
  };
}

const miles = (n: number) => n.toLocaleString("en-US");

/** The part generated from records, which is the part that must be right. */
export function listingHead(
  v: ListingVehicle,
  fees: ListingFees,
  priceCents: number,
  carfaxUrl?: string | null,
): string {
  const otd = outTheDoor(priceCents, fees);
  const name = [v.year, v.make, v.model, v.trim_level].filter(Boolean).join(" ");

  const lines = [
    `${name} — ${miles(v.odometer)} miles — ${formatMoney(priceCents)}`,
    "",
    "NO dealer fees. NO doc fees. NO hidden fees.",
    `Out the door for ${formatMoney(otd.withOwnTagCents)} if you bring a tag, `
      + `or ${formatMoney(otd.withNewTagCents)} with a new tag.`,
    "",
    /* Broken out because "out the door" invites the question. The fees are
       left unpriced on purpose — the number differs between a transfer and a
       new tag, and naming both invites a debate about which applies before
       the buyer has even seen the car. */
    `That is ${formatMoney(priceCents)} for the car, `
      + `${formatMoney(otd.taxCents)} Florida sales tax and the rest is DMV `
      + `fees for tag and title transfer, and you save yourself a trip to `
      + `the DMV.`,
  ];

  // Only when there is actually a report attached. An advertised "clean
  // Carfax" that nobody can open is worse than not mentioning it.
  if (carfaxUrl) {
    lines.push("", `Carfax: ${carfaxUrl}`);
  }

  return lines.join("\n");
}

/** Where a drafted list is inserted, underneath the checklist. */
export const EQUIPMENT_MARKER = "[EQUIPMENT]";

/**
 * The body you keep and edit.
 *
 * The equipment list is the dealer's own checklist, kept deliberately
 * over-complete — leather and cloth, sunroof and panoramic roof — because
 * deleting the lines that do not apply is faster than typing the ones that
 * do. It contradicts itself on purpose.
 *
 * A drafted list from the photographs goes in underneath, at the marker, so
 * the two never blur together: the checklist is what you assert, the draft is
 * what a camera saw.
 */
export const LISTING_BODY_TEMPLATE = `Clean title. Southern car, no rust, never smoked in.

This vehicle is in excellent condition, tires in excellent shape, no mechanical issues of any kind, no noises, weird smells or drivability issues.

Equipped with:

- Cold AC, Dual Zone
- Memory Seats
- Apple CarPlay and Android Auto
- Black interior trim with leather seats in great shape
- Tan cloth seats in great shape
- Heated Seats
- Cooled Seats
- Auto Headlights
- Power Seats
- Sunroof
- Dual glass panoramic roof
- Automatic Transmission
- Navigation system
- Bluetooth
- 18" wheels with good tires
- Powerful and fuel efficient 4 cylinder turbocharged 2.0L engine
- Power Folding Mirrors
- Car comes with 2 keys!
${EQUIPMENT_MARKER}

Everything works as it should, no active warning lights or check engine lights. Needs nothing and it is ready to go.

Every car we sell gets a full inspection and an oil change in our own shop before it goes out. We do not sell a car without them.

Included free: a 3 month / 3,000 mile powertrain warranty.

Sold by a dealer with absolutely no dealer fees or hidden fees of any kind.`;

/**
 * Below this, credit unions will not write a loan worth their paperwork, so
 * mentioning pre-approvals on a cheap car invites calls that go nowhere.
 */
export const FINANCE_FLOOR_CENTS = 700_000;

/**
 * The closing lines, generated rather than typed.
 *
 * These depend on the price, so they cannot live in the editable body: a
 * description written when a car was $7,500 would still promise credit union
 * financing after it was marked down to $6,200. Generated here, they follow
 * the price without anybody having to remember.
 */
export function listingFooter(priceCents: number): string {
  const lines: string[] = [];
  if (priceCents >= FINANCE_FLOOR_CENTS) {
    lines.push(
      "We do not offer financing, however, we will work with your "
      + "pre-approved Credit Union loans, especially Suncoast.");
  }
  lines.push("Price is negotiable. The free warranty is included at full price only.");
  return lines.join("\n\n");
}

/**
 * Put a drafted equipment list where the marker is.
 *
 * Appends instead if the marker has been edited away, so a draft is never
 * silently dropped.
 */
export function withEquipment(body: string, equipment: string): string {
  const list = equipment.trim();
  return body.includes(EQUIPMENT_MARKER)
    ? body.replace(EQUIPMENT_MARKER, list)
    : `${body.trimEnd()}\n\n${list}`;
}

/** Head and body together, ready to paste. */
/** Strip the marker for anyone reading the listing before a draft exists. */
export function cleanMarker(body: string): string {
  return body.replace(new RegExp(`\\n?${EQUIPMENT_MARKER.replace(/[[\]]/g, "\\$&")}`, "g"), "");
}

export function buildListing(
  v: ListingVehicle,
  fees: ListingFees,
  priceCents: number,
  body: string,
  carfaxUrl?: string | null,
): string {
  /* The VIN goes last. Buyers who want it will run it themselves, and it
     belongs at the bottom rather than competing with the price at the top. */
  return `${listingHead(v, fees, priceCents, carfaxUrl)}\n\n`
       + `${cleanMarker(body).trim()}\n\n`
       + `${listingFooter(priceCents)}\n\n`
       + `VIN: ${v.vin}\n`;
}
